import { state as appMainState, reducer as appMainReducer } from './app/main';
import { state as appLoaderState, reducer as appLoaderReducer } from './app/loader';
import { state as appFilterState, reducer as appFilterReducer } from './app/filter';
import { state as appSorterState, reducer as appSorterReducer } from './app/sorter';
import { state as appPaginatorState, reducer as appPaginatorReducer } from './app/paginator';
import { state as appExporterState, reducer as appExporterReducer } from './app/exporter';
import { state as appImporterState, reducer as appImporterReducer } from './app/importer';
import { state as appErrorLoggerState, reducer as appErrorLoggerReducer } from './app/error-logger';
import { state as entityMainState, reducer as entityMainReducer, gridActions as entityMainGridActions } from './entities/main';
import { state as entityLockerState, reducer as entityLockerReducer, gridActions as entityLockerGridActions } from './entities/locker';
import {
    state as entitySafeDeleterState,
    reducer as entitySafeDeleterReducer,
    gridActions as entitySafeDeleterGridActions
} from './entities/safe-deleter';
import {
    state as entityArchiverState,
    reducer as entityArchiverReducer,
    gridActions as entityArchiverGridActions
} from './entities/archiver';
import {
    state as entityWorkflowState,
    reducer as entityWorkflowReducer,
    gridActions as entityWorkflowGridActions
} from './entities/workflow';

const APP_BEHAVIORS = {
    main: {
        state: appMainState,
        reducer: appMainReducer
    },
    loader: {
        state: appLoaderState,
        reducer: appLoaderReducer
    },
    filter: {
        state: appFilterState,
        reducer: appFilterReducer
    },
    sorter: {
        state: appSorterState,
        reducer: appSorterReducer
    },
    paginator: {
        state: appPaginatorState,
        reducer: appPaginatorReducer
    },
    exporter: {
        state: appExporterState,
        reducer: appExporterReducer
    },
    importer: {
        state: appImporterState,
        reducer: appImporterReducer
    },
    'error-logger': {
        state: appErrorLoggerState,
        reducer: appErrorLoggerReducer
    }
};

const ENTITY_BEHAVIORS = {
    main: {
        state: entityMainState,
        reducer: entityMainReducer,
        gridActions: entityMainGridActions
    },
    locker: {
        state: entityLockerState,
        reducer: entityLockerReducer,
        gridActions: entityLockerGridActions
    },
    'safe-deleter': {
        state: entitySafeDeleterState,
        reducer: entitySafeDeleterReducer,
        gridActions: entitySafeDeleterGridActions
    },
    archiver: {
        state: entityArchiverState,
        reducer: entityArchiverReducer,
        gridActions: entityArchiverGridActions
    },
    workflow: {
        state: entityWorkflowState,
        reducer: entityWorkflowReducer,
        gridActions: entityWorkflowGridActions
    }
};

const normalizeCapabilities = (capabilities = {}) => ({
    app: Array.isArray(capabilities?.app) ? capabilities.app : [],
    entity: Array.isArray(capabilities?.entity) ? capabilities.entity : []
});

const mergeSegments = (registry, behaviorKeys = [], segmentKey = '') =>
    ['main', ...behaviorKeys].reduce((result, behaviorKey) => {
        const segment = registry?.[behaviorKey]?.[segmentKey];

        if (segment) {
            Object.assign(result, segment);
        }

        return result;
    }, {});

export const appState = (capabilities = {}) => {
    const normalizedCapabilities = normalizeCapabilities(capabilities);

    return {
        ...mergeSegments(APP_BEHAVIORS, normalizedCapabilities.app, 'state'),
        ...mergeSegments(ENTITY_BEHAVIORS, normalizedCapabilities.entity, 'state')
    };
};

export const appReducer = (capabilities = {}) => {
    const normalizedCapabilities = normalizeCapabilities(capabilities);

    return {
        ...mergeSegments(APP_BEHAVIORS, normalizedCapabilities.app, 'reducer'),
        ...mergeSegments(ENTITY_BEHAVIORS, normalizedCapabilities.entity, 'reducer')
    };
};

export const appGridActions = (capabilities = {}, row = {}, permissionData = [], onClickActions = {}) => {
    const normalizedCapabilities = normalizeCapabilities(capabilities);

    return ['main', ...normalizedCapabilities.entity].reduce((result, behaviorKey) => {
        const behaviorGridActions = ENTITY_BEHAVIORS?.[behaviorKey]?.gridActions;

        if (typeof behaviorGridActions === 'function') {
            return [...result, ...behaviorGridActions(row, permissionData, onClickActions)];
        }

        return result;
    }, []);
};
